import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, Tag, Spin, Tooltip, ButtonGroup, Button, Empty, Banner, Toast, Table, Input,
} from "@douyinfe/semi-ui";
import { IconSearch } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();

// ============ 类型（与后端 TeamPortraitVO 对齐） ============
interface TeamPortraitData {
  summary: {
    totalAssignedNew: number;
    totalFollowed: number;
    followRate: number;
    oneHourFollowRate: number;
    overdueCount: number;
    overdueGt24h: number;
    todayVisit: number;
    monthVisit: number;
    monthSigned: number;
    assignedDeltaPercent: number;
    oneHourFollowedCount: number;
    dayFollowedCount: number;
    unfollowedCount: number;
    totalAssignedOld: number;
    totalFollowedOld: number;
    oldFollowRate: number;
    oldOverdueCount: number;
    todayVisitOld: number;
    monthVisitOld: number;
    signedOld: number;
    oldAssignedDeltaPercent: number;
    old24hFollowedCount: number;
    oldLateFollowedCount: number;
    oldUnfollowedCount: number;
  };
  abnormalList: Array<{
    userId: number;
    userName: string;
    teamName: string;
    abnormalType: "OVERDUE" | "LOW_FOLLOW_RATE";
    description: string;
  }>;
  rows: Array<{
    userId: number;
    userName: string;
    teamName: string;
    roleName: string;
    todayAssignedNew: number;
    todayFollowNew: number;
    followRate: number;
    oneHourFollowRate: number;
    overdueCount: number;
    todayVisit: number;
    signed: number;
    oneHourFollowedCount: number;
    dayFollowedCount: number;
    unfollowedCount: number;
    todayAssignedOld: number;
    todayFollowOld: number;
    monthAssignedOld: number;
    oldFollowRate: number;
    oldOverdueCount: number;
    todayVisitOld: number;
    signedOld: number;
    old24hFollowedCount: number;
    oldLateFollowedCount: number;
    oldUnfollowedCount: number;
  }>;
  meta: {
    teams: Array<{ id: number; name: string }> | null;
  };
}

// ============ 设计 Token（沿用 userProfile） ============
const T = {
  color: {
    primary: "#1e2b5a",
    primarySoft: "#4f6faa",
    text: "#0f172a",
    textSub: "#475569",
    textMuted: "#94a3b8",
    bg: "#f4f6fb",
    cardBg: "#ffffff",
    itemBg: "#f9fafc",
    border: "#e2e8f0",
    success: "#16a34a",
    successBg: "#dcfce7",
    warning: "#f59e0b",
    warningBg: "#fef3c7",
    danger: "#dc2626",
    dangerBg: "#fef2f2",
  },
  fontSize: { xs: 12, sm: 13, md: 15, lg: 20, xl: 28 },
  radius: { sm: 8, md: 12, lg: 20 },
  space: { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24 },
} as const;

const fontFamily = "'Inter', 'PingFang SC', 'Microsoft YaHei', system-ui, -apple-system, sans-serif";

const cardStyle: React.CSSProperties = {
  background: T.color.cardBg, borderRadius: T.radius.lg, padding: T.space[5],
  boxShadow: "0 8px 20px rgba(0,0,0,0.04), 0 2px 6px rgba(0,20,40,0.04)",
  border: "none", fontFamily,
};

const PERIOD_LABEL: Record<string, string> = { today: "今日", week: "本周", month: "本月" };

// ============ 染色工具 ============
function rateCellColor(rate: number | null | undefined): string {
  if (rate == null) return "transparent";
  if (rate >= 90) return T.color.successBg;
  if (rate >= 70) return T.color.warningBg;
  return T.color.dangerBg;
}

function overdueCellColor(count: number | null | undefined): string {
  if (!count) return T.color.successBg;
  if (count <= 2) return T.color.warningBg;
  return T.color.dangerBg;
}

// ============ 主组件 ============
export default function TeamPortrait() {
  const navigate = useNavigate();
  const [data, setData] = useState<TeamPortraitData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>("today");
  const [scene, setScene] = useState<"new" | "old">("new");
  const [teamId, setTeamId] = useState<number | null>(null);
  const [keyword, setKeyword] = useState<string>("");
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    window.document.title = "销售画像";
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = { period };
        if (teamId != null) params.teamId = String(teamId);
        const resp = await apiClient.request<TeamPortraitData>(
          "/sales-portrait/team", "GET", params,
        );
        if (cancelled) return;
        if (resp.code === "ok") {
          setData(resp.data);
        } else {
          setError(resp.msg || "加载失败");
          Toast.error(resp.msg || "加载失败");
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg = e?.message || "网络异常，请稍后重试";
          setError(msg);
          Toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [period, teamId, reloadNonce]);

  // 前端按关键字过滤
  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (!keyword.trim()) return data.rows;
    const k = keyword.trim().toLowerCase();
    return data.rows.filter(r =>
      r.userName?.toLowerCase().includes(k) || r.teamName?.toLowerCase().includes(k),
    );
  }, [data?.rows, keyword]);

  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div style={{ padding: T.space[6], maxWidth: 1340, margin: "0 auto" }}>
        <Banner type="danger" description={error} />
        <div style={{ marginTop: T.space[4], textAlign: "center" }}>
          <Button onClick={() => setReloadNonce(n => n + 1)} theme="solid" type="primary">重试</Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { summary, abnormalList, meta } = data;
  const periodLabel = PERIOD_LABEL[period] ?? "今日";
  const isNew = scene === "new";
  const deltaText = summary.assignedDeltaPercent > 0
    ? `↑ 较上一周期 +${summary.assignedDeltaPercent.toFixed(1)}%`
    : summary.assignedDeltaPercent < 0
      ? `↓ 较上一周期 ${summary.assignedDeltaPercent.toFixed(1)}%`
      : "较上一周期持平";
  const oldDeltaText = summary.oldAssignedDeltaPercent > 0
    ? `↑ 较上一周期 +${summary.oldAssignedDeltaPercent.toFixed(1)}%`
    : summary.oldAssignedDeltaPercent < 0
      ? `↓ 较上一周期 ${summary.oldAssignedDeltaPercent.toFixed(1)}%`
      : "较上一周期持平";

  // ============ KPI 卡（新老客镜像） ============
  const kpiCards = isNew ? [
    {
      label: `${periodLabel}新客分配`, value: summary.totalAssignedNew,
      sub: <span style={{ color: summary.assignedDeltaPercent > 0 ? T.color.success : T.color.textSub }}>{deltaText}</span>,
    },
    {
      label: `${periodLabel}已跟进`, value: summary.totalFollowed,
      sub: <span style={{ color: T.color.textSub }}>队列响应率 {summary.followRate.toFixed(0)}%</span>,
    },
    {
      label: "1h跟进率", value: `${summary.oneHourFollowRate.toFixed(0)}%`,
      sub: <span style={{ color: summary.oneHourFollowRate >= 90 ? T.color.success : T.color.danger }}>
        目标 90% · 差距 {Math.max(0, 90 - summary.oneHourFollowRate).toFixed(0)}%
      </span>,
    },
    {
      label: "超时未跟", value: summary.overdueCount,
      sub: <span style={{ color: T.color.danger }}>其中 {summary.overdueGt24h} 人 {">"}24h</span>,
    },
    {
      label: `${periodLabel}邀约上门`, value: summary.todayVisit,
      sub: <span style={{ color: T.color.textSub }}>本月累计 {summary.monthVisit}</span>,
    },
    {
      label: "本月签约放款", value: summary.monthSigned,
      sub: <span style={{ color: T.color.success }}>本月累计</span>,
    },
  ] : [
    {
      label: `${periodLabel}老客分配`, value: summary.totalAssignedOld,
      sub: <span style={{ color: summary.oldAssignedDeltaPercent > 0 ? T.color.success : T.color.textSub }}>{oldDeltaText}</span>,
    },
    {
      label: `${periodLabel}老客跟进`, value: summary.totalFollowedOld,
      sub: <span style={{ color: T.color.textSub }}>24h队列响应率 {summary.oldFollowRate.toFixed(0)}%</span>,
    },
    {
      label: "24h跟进率", value: `${summary.oldFollowRate.toFixed(0)}%`,
      sub: <span style={{ color: summary.oldFollowRate >= 90 ? T.color.success : T.color.danger }}>
        目标 90% · 差距 {Math.max(0, 90 - summary.oldFollowRate).toFixed(0)}%
      </span>,
    },
    {
      label: "老客超时", value: summary.oldOverdueCount,
      sub: <span style={{ color: T.color.danger }}>再分配 {">"}24h 未跟</span>,
    },
    {
      label: `${periodLabel}老客上门`, value: summary.todayVisitOld,
      sub: <span style={{ color: T.color.textSub }}>本月累计 {summary.monthVisitOld}</span>,
    },
    {
      label: "本月老客签约", value: summary.signedOld,
      sub: <span style={{ color: T.color.success }}>本月累计</span>,
    },
  ];

  // ============ 表格列定义（新老客镜像） ============
  const baseColumns = [
    {
      title: "销售", dataIndex: "userName", key: "userName", width: 120,
      render: (text: string, row: any) => (
        <span style={{ fontWeight: 600, color: T.color.text }}>
          {text}
          <span style={{ fontSize: T.fontSize.xs, color: T.color.textMuted, marginLeft: 6 }}>
            {row.roleName}
          </span>
        </span>
      ),
    },
    { title: "团队", dataIndex: "teamName", key: "teamName", width: 100 },
  ];

  const actionColumn = {
    title: "操作", key: "action", width: 90,
    render: (_: any, row: any) => (
      <Button
        theme="borderless"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/userProfile?userId=${row.userId}&from=teamPortrait&period=${period}`);
        }}
      >
        查看 →
      </Button>
    ),
  };

  const rateCell = (v: number, tooltip: React.ReactNode) => (
    <Tooltip position="top" content={tooltip}>
      <span style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 4,
        background: rateCellColor(v), fontWeight: 600,
      }}>
        {v?.toFixed(0)}%
      </span>
    </Tooltip>
  );

  const overdueCell = (v: number) => (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      background: overdueCellColor(v), fontWeight: 600,
    }}>
      {v ?? 0}
    </span>
  );

  const newMetricColumns = [
    {
      title: "新客", dataIndex: "todayAssignedNew", key: "todayAssignedNew", width: 80,
      sorter: (a: any, b: any) => a.todayAssignedNew - b.todayAssignedNew,
      defaultSortOrder: "descend" as const,
    },
    { title: "已跟进", dataIndex: "todayFollowNew", key: "todayFollowNew", width: 80 },
    {
      title: (
        <Tooltip content="队列口径：分母为周期内分配且已过 60 分钟宽限期的客户；悬停数值查看 ≤1h / ≤24h / 未跟 分桶">
          <span>跟进率 (?)</span>
        </Tooltip>
      ),
      dataIndex: "followRate", key: "followRate", width: 90,
      render: (v: number, row: any) => rateCell(
        v,
        `≤1h 首响 ${row.oneHourFollowedCount ?? 0} · 61min~24h ${row.dayFollowedCount ?? 0} · 未跟 ${row.unfollowedCount ?? 0}`,
      ),
    },
    {
      title: "1h率", dataIndex: "oneHourFollowRate", key: "oneHourFollowRate", width: 90,
      render: (v: number) => rateCell(v, `本月新客 1h 首响占比`),
    },
    {
      title: "超时", dataIndex: "overdueCount", key: "overdueCount", width: 80,
      render: (v: number) => overdueCell(v),
    },
    { title: "上门", dataIndex: "todayVisit", key: "todayVisit", width: 80 },
    { title: "签约", dataIndex: "signed", key: "signed", width: 80 },
  ];

  const oldMetricColumns = [
    {
      title: "老客", dataIndex: "todayAssignedOld", key: "todayAssignedOld", width: 80,
      sorter: (a: any, b: any) => a.todayAssignedOld - b.todayAssignedOld,
      defaultSortOrder: "descend" as const,
    },
    { title: "已跟进", dataIndex: "todayFollowOld", key: "todayFollowOld", width: 80 },
    {
      title: (
        <Tooltip content="24h 队列口径：分母为周期内再分配且已过 24 小时宽限期的老客；悬停数值查看 ≤24h / >24h / 未跟 分桶">
          <span>24h跟进率 (?)</span>
        </Tooltip>
      ),
      dataIndex: "oldFollowRate", key: "oldFollowRate", width: 100,
      render: (v: number, row: any) => rateCell(
        v,
        `≤24h 首响 ${row.old24hFollowedCount ?? 0} · >24h 首响 ${row.oldLateFollowedCount ?? 0} · 未跟 ${row.oldUnfollowedCount ?? 0}`,
      ),
    },
    {
      title: "超时", dataIndex: "oldOverdueCount", key: "oldOverdueCount", width: 80,
      render: (v: number) => overdueCell(v),
    },
    { title: "上门", dataIndex: "todayVisitOld", key: "todayVisitOld", width: 80 },
    { title: "签约", dataIndex: "signedOld", key: "signedOld", width: 80 },
  ];

  const columns: any[] = [...baseColumns, ...(isNew ? newMetricColumns : oldMetricColumns), actionColumn];

  return (
    <div style={{
      padding: T.space[5], backgroundColor: T.color.bg, minHeight: "100vh",
      maxWidth: 1440, margin: "0 auto", fontFamily, position: "relative",
    }}>
      {loading && (
        <div style={{
          position: "absolute", top: T.space[5], right: T.space[5], zIndex: 10,
          background: "rgba(255,255,255,0.85)",
          padding: `${T.space[1]}px ${T.space[3]}px`,
          borderRadius: T.radius.sm, boxShadow: `0 2px 6px rgba(0,0,0,0.08)`,
          display: "flex", alignItems: "center", gap: T.space[2],
          fontSize: T.fontSize.xs, color: T.color.textSub,
        }}>
          <Spin /> 刷新中…
        </div>
      )}

      {/* ========== Filter Bar ========== */}
      <Card style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: T.space[3] }}>
          <div style={{ display: "flex", alignItems: "center", gap: T.space[3] }}>
            <h2 style={{ fontWeight: 700, fontSize: T.fontSize.lg, margin: 0, color: T.color.text, fontFamily }}>
              销售画像（{isNew ? "新客" : "老客"}） · {teamId ? (meta.teams?.find(t => t.id === teamId)?.name ?? "指定团队") : "全公司"}
            </h2>
            <span style={{ color: T.color.textSub, fontSize: T.fontSize.sm }}>
              | 共 {filteredRows.length} 名销售
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
            {meta.teams && meta.teams.length > 0 && (
              <>
                <span style={{ fontSize: T.fontSize.xs, color: T.color.textSub }}>团队：</span>
                <select
                  value={teamId ?? 0}
                  onChange={(e) => setTeamId(Number(e.target.value) || null)}
                  style={{
                    padding: "4px 10px", borderRadius: T.radius.sm, border: `1px solid ${T.color.border}`,
                    fontSize: T.fontSize.xs, background: T.color.itemBg,
                  }}
                >
                  <option value={0}>全部团队</option>
                  {meta.teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            )}
            <ButtonGroup size="small" theme="borderless">
              <Button onClick={() => setScene("new")} type={isNew ? "primary" : "tertiary"}>新客</Button>
              <Button onClick={() => setScene("old")} type={!isNew ? "primary" : "tertiary"}>老客</Button>
            </ButtonGroup>
            <ButtonGroup size="small" theme="borderless">
              <Button onClick={() => setPeriod("today")} type={period === "today" ? "primary" : "tertiary"}>今日</Button>
              <Button onClick={() => setPeriod("week")} type={period === "week" ? "primary" : "tertiary"}>本周</Button>
              <Button onClick={() => setPeriod("month")} type={period === "month" ? "primary" : "tertiary"}>本月</Button>
            </ButtonGroup>
            <Input
              prefix={<IconSearch />}
              placeholder="搜索销售/团队"
              value={keyword}
              onChange={(v) => setKeyword(v)}
              style={{ width: 180 }}
            />
          </div>
        </div>
      </Card>

      <div style={{ height: T.space[4] }} />

      {/* ========== Abnormal Banner（口径基于新客，仅新客 Tab 展示） ========== */}
      {isNew && abnormalList.length > 0 && (
        <div style={{
          background: T.color.dangerBg, borderLeft: `3px solid ${T.color.danger}`,
          borderRadius: T.radius.sm, padding: T.space[3], marginBottom: T.space[4],
          fontSize: T.fontSize.sm, color: "#7f1d1d",
        }}>
          <strong>⚠️ 需关注 {abnormalList.length} 人：</strong>
          {abnormalList.slice(0, 5).map(a => (
            <Tag
              key={a.userId}
              size="small"
              color="red"
              shape="circle"
              style={{ margin: `0 ${T.space[1]}px`, cursor: "pointer" }}
              onClick={() => navigate(`/userProfile?userId=${a.userId}&from=teamPortrait&period=${period}`)}
            >
              {a.userName}（{a.description}）
            </Tag>
          ))}
          {abnormalList.length > 5 && <span style={{ marginLeft: T.space[2] }}>等 {abnormalList.length - 5} 人</span>}
        </div>
      )}

      {/* ========== KPI Cards ========== */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: T.space[3], marginBottom: T.space[4] }}>
        {kpiCards.map(card => (
          <Card key={card.label} style={{ ...cardStyle, padding: T.space[4] }}>
            <div style={{ fontSize: T.fontSize.xs, color: T.color.textSub, marginBottom: T.space[1] }}>{card.label}</div>
            <div style={{ fontSize: T.fontSize.xl, fontWeight: 700, color: T.color.text, lineHeight: 1.1 }}>
              {card.value}
            </div>
            <div style={{ fontSize: T.fontSize.xs, marginTop: T.space[1] }}>{card.sub}</div>
          </Card>
        ))}
      </div>

      {/* ========== Matrix Table ========== */}
      <Card style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: T.space[3] }}>
          <h3 style={{ fontWeight: 600, fontSize: T.fontSize.md, color: T.color.text, margin: 0, fontFamily }}>
            销售明细 · {isNew ? "新客" : "老客"}口径（点击"查看"或销售名下钻单人画像）
          </h3>
          <span style={{ fontSize: T.fontSize.xs, color: T.color.textSub }}>
            染色：🟩 优 · 🟨 关注 · 🟥 异常
          </span>
        </div>
        <Table
          columns={columns}
          dataSource={filteredRows}
          rowKey="userId"
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
          empty={<Empty description="该范围内暂无销售数据" />}
          onRow={(row) => ({
            onClick: () => navigate(`/userProfile?userId=${row.userId}&from=teamPortrait&period=${period}`),
            style: { cursor: "pointer" },
          })}
        />
      </Card>
    </div>
  );
}
